import { Box, Button, Grid, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from 'src/components/common/Chip'
import { IDeviation } from 'src/generated-types/deviation'
import { IDeviationKeywords } from 'src/generated-types/deviation-keywords'
import { IDeviationMeasure } from 'src/generated-types/deviation-measure'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { DeviationSectionCard } from './DeviationSectionCard'

const DESCRIPTION_CLAMP_LINES = 4
const DESCRIPTION_CLAMP_THRESHOLD = 240

type Props = {
  deviation?: IDeviation
  spreadLayout?: boolean
}

const EyebrowLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography
      sx={{
        fontSize: '13px',
        fontWeight: 500,
        color: getThemeColor(theme, MUIThemeColors.secondaryMain),
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  )
}

export const DeviationDetailsPanel: React.FC<Props> = ({ deviation, spreadLayout }) => {
  const { t } = useTranslation(['deviation', 'common'])
  const theme = useTheme()
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  const hasMeasure =
    deviation?.measure != null && IDeviationMeasure[deviation.measure as IDeviationMeasure] !== undefined
  const hasKeywords = !!(deviation?.keywordsArray && deviation.keywordsArray.length > 0)
  const hasFooter = hasMeasure || hasKeywords

  const shouldClampDescription =
    !!deviation?.description && deviation.description.length > DESCRIPTION_CLAMP_THRESHOLD

  const descriptionBlock = deviation?.description && (
    <Box>
      <EyebrowLabel>{t('description', { ns: 'common' })}</EyebrowLabel>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.5,
          color: getThemeColor(theme, MUIThemeColors.primaryMain),
          whiteSpace: 'pre-wrap',
          ...(shouldClampDescription && !descriptionExpanded
            ? {
                display: '-webkit-box',
                WebkitLineClamp: DESCRIPTION_CLAMP_LINES,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {deviation.description}
      </Typography>
      {shouldClampDescription && (
        <Button
          size="small"
          variant="text"
          onClick={() => setDescriptionExpanded(prev => !prev)}
          sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none', mt: 0.25 }}
        >
          {descriptionExpanded ? t('showLess', { ns: 'common' }) : t('showMore', { ns: 'common' })}
        </Button>
      )}
    </Box>
  )

  const reasonBlock = deviation?.reason && (
    <Box>
      <EyebrowLabel>{t('reason', { ns: 'deviation' })}</EyebrowLabel>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.5,
          color: getThemeColor(theme, MUIThemeColors.primaryMain),
          whiteSpace: 'pre-wrap',
        }}
      >
        {deviation.reason}
      </Typography>
    </Box>
  )

  const measuresBlock = deviation?.measures && (
    <Box>
      <EyebrowLabel>{t('measures', { ns: 'deviation' })}</EyebrowLabel>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.5,
          color: getThemeColor(theme, MUIThemeColors.primaryMain),
          whiteSpace: 'pre-wrap',
        }}
      >
        {deviation.measures}
      </Typography>
    </Box>
  )

  const footerBlock = hasFooter && (
    <Flex.Row gap={3} sx={{ flexWrap: 'wrap' }}>
      {hasMeasure && (
        <Box>
          <EyebrowLabel>{t('measure', { ns: 'deviation' })}</EyebrowLabel>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: getThemeColor(theme, MUIThemeColors.primaryMain) }}>
            {t(IDeviationMeasure[deviation!.measure as IDeviationMeasure])}
          </Typography>
        </Box>
      )}
      {hasKeywords && (
        <Box>
          <EyebrowLabel>{t('keywords', { ns: 'common' })}</EyebrowLabel>
          <Flex.Row gap={0.5} sx={{ flexWrap: 'wrap' }}>
            {deviation!.keywordsArray!.map((keyword, index) => (
              <Chip key={index} variant="outlined" size="small" label={t(IDeviationKeywords[keyword])} />
            ))}
          </Flex.Row>
        </Box>
      )}
    </Flex.Row>
  )

  return (
    <DeviationSectionCard title={t('details', { ns: 'deviation' })} fullHeight>
      {spreadLayout ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Flex.Column gap={1.5}>
              {descriptionBlock}
              {reasonBlock}
            </Flex.Column>
          </Grid>
          <Grid item xs={12} md={6}>
            <Flex.Column gap={1.5}>
              {measuresBlock}
              {footerBlock}
            </Flex.Column>
          </Grid>
        </Grid>
      ) : (
        <Flex.Column gap={1.5}>
          {descriptionBlock}
          {reasonBlock}
          {measuresBlock}
          {footerBlock}
        </Flex.Column>
      )}
    </DeviationSectionCard>
  )
}
